<template>
  <div class="catalog-container">
    <div class="catalog-header">
      <v-text-field
        v-model="searchQuery"
        :placeholder="$t('purchaseV2.catalog.searchPlaceholder')"
        :disabled="isLoading"
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        clearable />
      <CatalogSkeletonLoader v-if="isLoading" />
      <v-alert v-else-if="error" type="error" dense class="mb-0">
        {{ error }}
      </v-alert>
      <div
        v-else-if="!categoryAndVariants.length"
        class="empty-state grey--text">
        {{ $t('purchaseV2.catalog.emptyState') }}
      </div>
      <v-tabs
        v-else-if="!isSearchActive"
        v-model="selectedCategoryCode"
        background-color="primary"
        show-arrows
        dark
        grow
        class="tabs mt-0">
        <v-tab
          v-for="{ category } in categoryAndVariants"
          :key="category.id"
          :href="`#${category.code}`"
          class="tab font-weight-bold">
          {{ category.name }}
        </v-tab>
        <v-tab
          v-if="arePromotionsEnabled"
          :href="`#${promoCode}`"
          class="tab font-weight-bold">
          {{ $t('entities.promotions.title') }}
        </v-tab>
      </v-tabs>
    </div>
    <div v-if="!isLoading && !error" class="catalog-content">
      <SearchList
        v-if="isSearchActive"
        :search-results="searchResults"
        :all-variants="allVariants"
        :arena-currency="arenaCurrency"
        @variant-quantity-change="emitVariantQuantityChange"
        @promotion-quantity-change="emitPromotionQuantityChange" />
      <template v-else>
        <div v-if="!isPromotionsTabSelected" class="catalog-content-grid">
          <CatalogVariantCard
            v-for="variant in categoryVariants"
            :key="variant.id"
            :variant="variant"
            :is-coins-variant="isCoinsTabSelected"
            :is-digital-item="isDigitalItemTab"
            :arena-currency="arenaCurrency"
            :quantity="getVariantQuantity(variant)"
            @update-quantity="emitVariantQuantityChange" />
        </div>
        <CatalogPromotions
          v-else-if="arePromotionsEnabled"
          :promotions="promotions"
          :variants="allVariants"
          :arena-currency="arenaCurrency"
          @quantity-change="emitPromotionQuantityChange"
          class="catalog-content-grid" />
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Category from '@/models/shop/CategoryV2'
import ArenaProduct from '@/models/shop/ArenaProduct'
import CatalogPromotions from './CatalogPromotions.vue'
import CatalogVariantCard from './VariantCard.vue'
import CatalogSkeletonLoader from './CatalogSkeletonLoader.vue'
import SearchList from './SearchList.vue'
import Variant from '@/models/shop/Variant'
import Promotion from '@/models/shop/Promotion'

export default {
  name: 'PurchaseCatalog',
  components: {
    CatalogVariantCard,
    CatalogSkeletonLoader,
    CatalogPromotions,
    SearchList,
  },
  props: {
    arenaCurrency: { type: Object, default: () => ({}) },
    isArenaCurrencyLoading: { type: Boolean, default: false },
  },
  data: () => ({
    areArenaVariantsLoading: false,
    arePromotionsLoading: false,
    categoryAndVariants: [],
    promotions: [],
    selectedCategoryCode: {},
    error: '',
    searchQuery: '',
  }),
  computed: {
    ...mapGetters('cart', ['cartItems']),
    promoCode: () => 'PROMO',
    arePromotionsEnabled() {
      return this.$store.getters.country !== 'ba'
    },
    isPromotionsTabSelected() {
      return this.selectedCategoryCode === this.promoCode
    },
    isCoinsTabSelected() {
      return this.selectedCategoryCode === 'CNS'
    },
    isPassTabSelected() {
      return this.selectedCategoryCode === 'PAS'
    },
    isDigitalItemTab() {
      return this.isCoinsTabSelected || this.isPassTabSelected
    },
    arenaId() {
      return this.$store.getters.arena?.id
    },
    isLoading() {
      return (
        this.isArenaCurrencyLoading ||
        this.areArenaVariantsLoading ||
        this.arePromotionsLoading
      )
    },
    allVariants() {
      return this.categoryAndVariants.flatMap((item) => item.variants)
    },
    categoryVariants() {
      return (
        this.categoryAndVariants?.find(
          (item) => item?.category?.code === this.selectedCategoryCode,
        )?.variants ?? []
      )
    },
    isSearchActive() {
      return !!this.searchQuery?.trim()
    },
    searchResults() {
      if (!this.isSearchActive) return []

      const query = this.searchQuery.trim().toLowerCase()
      const results = []

      // Search through variants
      this.allVariants.forEach((variant) => {
        if (variant.name?.toLowerCase().includes(query)) {
          results.push({ type: 'variant', data: variant })
        }
      })

      // Search through promotions
      if (this.arePromotionsEnabled) {
        this.promotions.forEach((promotion) => {
          if (promotion.name?.toLowerCase().includes(query)) {
            results.push({ type: 'promotion', data: promotion })
          }
        })
      }

      return results
    },
  },
  methods: {
    async fetchArenaVariants() {
      if (!this.arenaId) {
        this.categoryAndVariants = []
        return
      }
      this.areArenaVariantsLoading = true
      try {
        const params = {
          currency_id: this.arenaCurrency?.id,
        }
        const res = await ArenaProduct.api.getArenaVariants(
          this.arenaId,
          params,
        )
        const data = res.data?.data ?? []

        this.categoryAndVariants = data.map(({ category, variants }) => ({
          category: new Category(category),
          variants: variants.map((variant) => new Variant(variant)),
        }))
        this.error = ''
      } catch (error) {
        const fallbackMessage = 'Fetching categories with variants failed'
        console.error(fallbackMessage, error)
        this.categoryAndVariants = []
        this.error = error?.response?.data?.message || fallbackMessage
      } finally {
        const allVariants = this.categoryAndVariants.flatMap(
          ({ variants }) => variants ?? [],
        )
        this.$store.commit('cart/setAllCatalogVariants', allVariants)
        this.areArenaVariantsLoading = false
      }
    },
    async fetchPromotions() {
      if (!this.arenaId || !this.arePromotionsEnabled) {
        this.promotions = []
        return
      }
      this.arePromotionsLoading = true
      try {
        const res = await Promotion.api.list(this.arenaId, { active: true })
        const data = res.data?.data ?? []

        this.promotions = data
          .map((item) => new Promotion(item))
          .filter((item) => item.groups.length)
      } catch (error) {
        console.error(error)
        this.promotions = []
      } finally {
        this.arePromotionsLoading = false
      }
    },
    getVariantQuantity(variant) {
      const selectedVariant = this.cartItems.find(
        (item) => item.type === 'variant' && item.variant.id === variant.id,
      )
      if (!selectedVariant || selectedVariant.type !== 'variant') return 0
      return selectedVariant.quantity
    },
    emitVariantQuantityChange(payload) {
      this.$store.dispatch('cart/updateVariantQuantity', payload)
    },
    emitPromotionQuantityChange(payload) {
      this.$store.dispatch('cart/updatePromotionQuantity', payload)
    },
  },
  async created() {
    await Promise.all([this.fetchArenaVariants(), this.fetchPromotions()])
  },
}
</script>

<style scoped>
.catalog-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.catalog-header {
  flex-shrink: 0;
  padding-bottom: 1rem;
}

.catalog-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  padding: 0.125rem;
  margin-top: 1rem;
  padding-bottom: 1rem;
  /* Smooth scrolling */
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

.catalog-content::after {
  content: '';
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3.125rem;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.5) 30%,
    rgba(255, 255, 255, 0.85) 70%,
    rgba(255, 255, 255, 1) 100%
  );
  pointer-events: none;
  z-index: 1;
  margin-top: -3.125rem;
}

.tabs {
  border-radius: 4px;
  overflow: hidden;
  margin-top: 1rem;
}

.tabs::v-deep .v-tabs-slider {
  border-radius: 4px;
}

.catalog-content-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  padding-bottom: 0.5rem;
}

/* Responsive breakpoints */
@media (min-width: 600px) {
  .catalog-content-grid {
    gap: 1.25rem;
  }
}

/* 2 columns for medium screens */
@media (min-width: 1080px) {
  .catalog-content-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
}

/* 3 columns for large screens */
@media (min-width: 1500px) {
  .catalog-content-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
  }
}

.empty-state {
  text-align: center;
  margin-top: 1rem;
}
</style>
